/**
 * Analyzer: Day Drawdown
 *
 * Free-tier signal: warns when intraday max drawdown exceeds the absolute
 * value of today's close AND crosses $100. The point is "you were down
 * $400 even though you closed +$50" — a discipline/risk signal that has
 * NOTHING to do with historical baselines, so it doesn't belong inside
 * `dayBaselineCompare` (which is Pro-gated). Splitting it out lets free-tier
 * users see this important risk read.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayDrawdown';
export const tier = 'free';

const MIN_DRAWDOWN_DOLLARS = 100;

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.core) return null;
  // Live-trade gate: skip when no trades have closed today. v7.5.14 contract
  // extended to day-scope; see dayUtils.js for the closed-only core stats.
  if (day.core.closedTotal === 0) return null;

  const { totalPnL, maxDrawdown } = day.core;
  if (typeof maxDrawdown !== 'number' || !isFinite(maxDrawdown)) return null;

  // Only fire when intraday DD was meaningfully bigger than the final number.
  // This catches: deep underwater days that closed near flat or barely green.
  if (maxDrawdown <= Math.abs(totalPnL)) return null;
  if (maxDrawdown <= MIN_DRAWDOWN_DOLLARS) return null;

  return makeObservation({
    id: `${key}:${day.date}`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.RISK,
    title: 'Significant intraday drawdown',
    detail: `Max drawdown $${maxDrawdown.toFixed(0)} vs day P&L of ${totalPnL >= 0 ? '+' : '−'}$${Math.abs(totalPnL).toFixed(0)}. You were underwater more than the final number shows.`,
    action: 'Consider smaller size or tighter stops — recovering large red days uses up edge that could have stayed in the bank.',
    evidence: { totalPnL, maxDrawdown },
    metric: { label: 'Max DD', value: `$${maxDrawdown.toFixed(0)}`, unit: '' },
    confidence: 0.8,
    priority: 70
  });
};
